/** Project an HTML rectangle onto the four visible corners of a Spline screen. */
export interface Point { x: number; y: number }

export function screenTransform([a, b, c, d]: [Point, Point, Point, Point], width: number, height: number): string | null {
  const dx1 = b.x - c.x, dx2 = d.x - c.x, dx3 = a.x - b.x + c.x - d.x;
  const dy1 = b.y - c.y, dy2 = d.y - c.y, dy3 = a.y - b.y + c.y - d.y;
  const determinant = dx1 * dy2 - dx2 * dy1;
  if (Math.abs(determinant) < 0.001 || width <= 0 || height <= 0) return null;
  const g = (dx3 * dy2 - dx2 * dy3) / determinant;
  const h = (dx1 * dy3 - dx3 * dy1) / determinant;
  const matrix = [
    (b.x - a.x + g * b.x) / width, (b.y - a.y + g * b.y) / width, 0, g / width,
    (d.x - a.x + h * d.x) / height, (d.y - a.y + h * d.y) / height, 0, h / height,
    0, 0, 1, 0, a.x, a.y, 0, 1,
  ];
  return matrix.every(Number.isFinite) ? `matrix3d(${matrix.join(',')})` : null;
}

// Small, version-specific adapter for the installed Spline 2.0.54 controls API.
// These objects come from Application.controls, not a second Three.js scene.
export interface SceneVector {
  x: number; y: number; z: number;
  clone(): SceneVector;
  set(x: number, y: number, z: number): SceneVector;
  applyMatrix4(matrix: unknown): SceneVector;
  transformDirection(matrix: unknown): SceneVector;
  project(camera: SceneCamera): SceneVector;
}
export interface SceneObject {
  name: string;
  uuid: string;
  parent?: SceneObject;
  children: SceneObject[];
  position: SceneVector;
  matrixWorld: unknown;
  visible: boolean;
  geometry?: {
    computeBoundingBox(): void;
    boundingBox: { min: SceneVector; max: SceneVector } | null;
  };
  updateMatrix(): void;
  add(object: SceneObject): void;
  getObjectByName(name: string): SceneObject | undefined;
  updateWorldMatrix(updateParents: boolean, updateChildren: boolean): void;
}
export interface SceneCamera extends SceneObject {
  up: SceneVector;
  lookAt(target: SceneVector): void;
  updateMatrixWorld(): void;
}
export interface SceneControls {
  eventContext: { raycaster: {
    setFromCamera(point: Point, camera: SceneCamera): void;
    intersectObjects(objects: SceneObject[], recursive: boolean): { object: SceneObject }[];
  } };
  orbitControls?: { object: SceneCamera; target: SceneVector; enableZoom: boolean; enablePan: boolean; enabled: boolean };
}
